<template>
	<div class="text-center" :class="classList">
		<CAuthPageHeader
			:title="header"
			class="pb-3 light"
		/>
		<div v-if="step === 1">
			<div class="center pb-3">
				<label class="cog-label flex--center" for="job">
					What brings you here?
				</label>
			</div>

			<RadioGroup
				v-model="selectedJob"
				class="create-industry-organization__occupation flex gap-3 pb-4"
			>
				<RadioButton
					v-for="job in jobs"
					:key="job.value"
					class="p-2 pointer"
					:label="job.value"
				>
					<Inline :svg="`complex-icons/${job.icon}`" class="w-66" />
					<div class="sz-2">{{ job.value }}</div>
				</RadioButton>
			</RadioGroup>

			<Collapse v-model="selectedJob">
				<CollapseItem name="School" :disabled="true">
					<div class="pb-1">
						<label class="cog-label flex--center" for="role">
							What is your role?
						</label>
					</div>

					<CRadioButtonBar
						:display-value="selectedRole"
						class="create-industry-organization__school-radio-button-bar sz-3 pb-4 inline-block"
						:options="roles"
						@change="selectedRole = $event"
					>
					</CRadioButtonBar>
				</CollapseItem>
				<CollapseItem name="Work" :disabled="true">
					<div class="pb-3">
						<label class="cog-label flex--center" for="industry">
							What is your industry?
						</label>
					</div>

					<CDeprecatedSelect
						id="industry"
						v-model="selectedIndustry"
						class="pb-4"
						:options="workIndustries"
						popper-class="create-industry-organization__industry"
						filterable
					/>
				</CollapseItem>
			</Collapse>

			<div class="pb-3">
				<label class="cog-label flex--center gap-2" for="organization-name">
					What is your organization's name?
				</label>
			</div>

			<CDeprecatedValidatedInput
				id="organization-name"
				v-model.trim="organizationName"
				type="text"
				name="Organization Name"
				rules="required|string-length:0,170"
				class="pb-4"
			/>

			<CStatusButton
				class="button button--primary bg-cta sz-4 mt-2"
				title="Create Organization" type="submit" :status="continueButtonStatus"
				:disabled="shouldDisableContinue"
				@click.native="createOrg"
			>
				Continue
			</CStatusButton>
		</div>
		<div v-else-if="showTemplates">
			<CSelectTemplates
				:site-manifest="siteManifest"
				:selected-industry="selectedIndustry"
				@done="routeToRedirect"
			/>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent } from 'vue';
	import { globalStore } from 'src/stores/global-store';
	import { createOrganization } from '@cognitoforms/api/services/auth-service';
	import { RadioGroup, RadioButton, Collapse, CollapseItem } from '@cognitoforms/element-ui';
	import { NextStep } from '@cognitoforms/types/server-types/services/infrastructure/authentication/next-step';
	import session from '@cognitoforms/api/services/session';
	import CAuthPageHeader from 'src/components/auth/AuthPageHeader.vue';
	import CRadioButtonBar from 'src/components/form-controls/RadioButtonBar.vue';
	import CDeprecatedValidatedInput from 'src/components/form-controls/DeprecatedValidatedInput.vue';
	import CStatusButton from 'src/components/interaction/StatusButton.vue';
	import CDeprecatedSelect from 'src/components/form-controls/DeprecatedSelect.vue';
	import CSelectTemplates from 'src/views/SelectTemplates.vue';

	export default defineComponent({
		name: 'CreateOrganization',
		components: {
			CAuthPageHeader,
			CDeprecatedValidatedInput,
			CRadioButtonBar,
			CStatusButton,
			CDeprecatedSelect,
			CSelectTemplates,
			RadioGroup,
			RadioButton,
			Collapse,
			CollapseItem
		},
		inject: ['$store'],
		props: {
			siteManifest: {
				type: Object,
				default: null
			}
		},
		data() {
			return {
				organizationName: '',
				selectedJob: 'Work',
				selectedRole: 'teacher',
				selectedIndustry: null,
				jobs: [
					{
						value: 'Work',
						icon: 'work'
					},
					{
						value: 'School',
						icon: 'school'
					},
					{
						value: 'Personal',
						icon: 'account-organization'
					}
				],
				roles: [
					{
						displayValue: 'Teacher/Admin',
						value: 'teacher'
					},
					{
						displayValue: 'Student',
						value: 'student'
					}
				],
				industries: [],
				continueButtonStatus: 'default',
				redirectRoute: null,
				step: 1
			};
		},
		head() {
			return {
				title: 'Create Organization',
				meta: [
					{
						name: 'description',
						content: 'Cognito Forms, a free online form builder that helps you collect information and payments. Easily create feedback forms, payment forms, registration forms, and much more.'
					}
				]
			};
		},
		computed: {
			useAppNav() {
				return session.value?.enabledFeatures.AppNav;
			},
			header() {
				const firstName = this.useAppNav ? globalStore.user.FirstName : this.$store.profile.firstName;
				if (this.step === 1)
					return 'Welcome, ' + firstName + '!';
				return 'Select some templates to get started.';
			},
			classList() {
				if (this.step === 2)
					return 'select-templates';
				return 'create-industry-organization';
			},
			isTeacher() {
				return this.selectedJob === 'School' && this.selectedRole === 'teacher';
			},
			isStudent() {
				return this.selectedJob === 'School' && this.selectedRole === 'student';
			},
			isPersonal() {
				return this.selectedJob === 'Personal';
			},
			shouldDisableContinue() {
				return !this.organizationName || (!this.selectedIndustry && !(this.isTeacher || this.isStudent || this.isPersonal));
			},
			showTemplates() {
				return this.step === 2 && !this.isPersonal && !this.isStudent && !this.fromFormGeneration;
			},
			fromFormGeneration() {
				return this.redirectRoute?.includes('/l/ai-form-generation');
			},
			workIndustries() {
				return this.industries.filter(industry => industry.displayValue !== 'Personal');
			},
			defaultOrgName() {
				if (this.useAppNav)
					return globalStore.fullName;
				else
					return this.$store.profile.lastName ? this.$store.profile.name : this.$store.profile.firstName;
			}
		},
		watch: {
			selectedJob() {
				if (this.isPersonal && !this.organizationName) {
					// Default organization name to user name if empty
					this.organizationName = this.defaultOrgName;
				}
			},
			selectedRole() {
				if (this.isStudent && !this.organizationName) {
					// Default organization name to user name if empty
					this.organizationName = this.defaultOrgName;
				}
			}
		},
		mounted() {
			if (this.useAppNav)
				globalStore.loadUser();
			else
				this.$store.loadProfile();

			if (this.siteManifest) {
				this.industries = this.siteManifest.industries.list.all.map(industry => {
					return {
						displayValue: industry.title,
						value: industry.cfid
					};
				});
			}
		},
		methods: {
			createOrg() {
				this.continueButtonStatus = 'validating';

				// If school teacher, set industry category
				if (this.isTeacher) {
					this.selectedIndustry = this.industries.find(i => i.value === '1')?.value; // K-12 Education
				}
				// If personal or student, set industry category
				else if (this.isPersonal || this.isStudent) {
					this.selectedIndustry = this.industries.find(i => i.value === '35')?.value;
				}

				createOrganization(this.organizationName, this.selectedIndustry, this.industries.find(i => i.value === this.selectedIndustry)?.displayValue)
					.then(res => {
						if (res.NextStep === NextStep.Success) {
							this.redirectRoute = res.RedirectUrl;
						}

						// If industry is personal, redirect to return url and skip templates page
						const hasPersonalIndustry = this.industries.find(i => i.value === this.selectedIndustry)?.displayValue === 'Personal';
						if (hasPersonalIndustry)
							return window.location.assign(this.redirectRoute);

						// If signing up from a template, use return url to copy and skip templates page
						if (this.redirectRoute.includes('source=')) {
							const returnUrl = new URL(this.redirectRoute);
							const searchParams = returnUrl.search.split('&');
							const source = searchParams.filter(p => p.includes('source='))[0];
							if (this.fromFormGeneration || source.includes('template') || source.includes('jotform'))
								return window.location.assign(this.redirectRoute);
						}
						// Otherwise, continue to templates page for selected industry
						this.step = 2;

						this.continueButtonStatus = 'default';
					})
					.catch(e => {
						console.warn(e);
						this.continueButtonStatus = 'error';
					});
			},
			routeToRedirect() {
				return window.location.assign(this.redirectRoute);
			}
		}
	});
</script>

<style lang="scss">
.create-industry-organization {

	[data-auth-style='app'] .social-login & h1 {
		font-family: var(--font-display-light);
	}

	// TODO: Add gap class to utility classes
	// Do after AMP is killed since it will increase amp css slightly and probably cause erros
	.gap-2 {
		gap: $gutter * .25;
	}

	.gap-3 {
		gap: $gutter * .5;
	}

	&__occupation {

		input {
			display: none;
		}

		label {
			min-height: 96px;
			border: 1px solid var(--border-primary);
		}

		.is-active {
			background: var(--bg-selected-nav);
			border-color: var(--border-selected-nav-intense);

			svg [fill] {
				fill: var(--border-selected-nav);
			}
		}

		label:not(.is-active):hover {
			background: var(--bg-hovered);
			transition: background-color 1s;

			svg [fill] {
				transition: background-color .4s;
				fill: var(--accent-5);
			}
		}
	}

	&__school-radio-button-bar .el-radio-button__inner {
		// Tab bar is slightly taller than input. Shave off a pixel here to prevent a jitter.
		padding-top: 9px;
	}
}

.el-collapse-item {

	&__wrap {
		overflow: hidden;
		will-change: height;
		transition: height .25s;
	}
}

.select-templates {
	align-content: center;
}

[data-auth-style='app'] .social-login {

	.faux-modal__body {
		@include contain-until($b5);
		padding: $space__5;
		padding-bottom: 50px;
	}

	.faux-modal__wrapper {
		max-width: 320px;
	}

	h1 {
		font-size: $size__7;
		font-weight: 300;
	}

	.el-input__inner {
		min-height: 40px;
		padding-left: 1em;
	}

	.button--primary {
		width: 100%;
	}

	.cog-label {
		margin-bottom: 0;
		font-size: $size__3;
		font-weight: $weight__text--normal;
	}

	.template-selection__grid {
		margin-bottom: $space__5;
	}
}
</style>